import equal from 'fast-deep-equal';
import {
    ChevronIcon,
    Column,
    Container,
    cssVar,
    DropDown,
    Focused,
    Grid,
    Label,
    Link,
    Row,
    SecondaryButton,
    Separator,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
import React, { memo, useState } from 'react';
import { useIntl } from 'react-intl';
import styled, { css, keyframes } from 'styled-components';

import ProductClassIcon from '../../../ProductClassIcon';
import Avatar from '../Avatar';
import InsurabilityScoreIcon from '../InsurabilityScoreIcon';

const glow = keyframes`{
    from {
      box-shadow: 0 0 3px -3px ${cssVar.colorPrimary};
    }
    to {
      box-shadow: 0 0 3px 3px ${cssVar.colorPrimary};
    }
  }`;

const CardComponent = styled(Container)`
	border: solid 1px ${cssVar.colorStrokeLight};
	max-width: ${({ isMobile, isTablet }) => (isMobile ? 'calc(50vw - 15px)' : isTablet ? 'calc(25vw - 20px)' : '222px')};
	min-width: ${({ isMobile, isTablet }) => (isMobile ? 'calc(50vw - 15px)' : isTablet ? 'calc(25vw - 20px)' : '222px')};
    ${({ priority }) => priority && css`animation: ${glow} 1s infinite alternate;`}
`;

const AvatarContainer = styled(Container)`
	width: 23px;
	height: 23px;
`;

const NameContainer = styled(Container)`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledAddPointsSecondayButton = styled(SecondaryButton)`
	opacity: 1;
	margin-left:0;
	font-weight:500;
    font-size: 13px;
	height: auto;
	width:100%;
	@media {min-width: 12px;}
`;

const BoardCard = memo(
    (props) => {
        const alignments = useAlignments();
        const sizes = useSizes();
        const screen = useScreen();
        const intl = useIntl();

        const [collapsed, setCollapsed] = useState(true);
        const [assignable, setAssignable] = useState(false);
        const [_productEditable, _setProductEditable] = useState(false);
        const [_brokerCompanyAssignable, setBrokerCompanyAssignable] = useState(false);

        // Function to update child reference as per parent value
        const updateChildRef = (childRef, code) => {
            let ref = childRef;

            if (code && ref) {
                ref = ref.filter((x) => x.elements[0].value === code);
            }

            return ref;
        };

        const policy = props.policy;

        // Get translated texts
        const unassignedText = intl.formatMessage({ id: 'Policies_Board.card.unassigned' });
        const statusText = intl.formatMessage({ id: 'Policies_Board.card.status' });
        const productText = intl.formatMessage({ id: 'Policies_Board.card.product' });
        const effectiveText = intl.formatMessage({ id: 'Policies_Board.card.effective' });
        const descriptionText = intl.formatMessage({ id: 'Policies_Board.card.description' });
        const addressText = intl.formatMessage({ id: 'Policies_Board.card.address' });
        const callText = intl.formatMessage({ id: 'Policies_Board.card.call' });
        const emailText = intl.formatMessage({ id: 'Policies_Board.card.email' });

        const assignees = (
            <Focused leaveEvent={() => setAssignable(false)}>
                <DropDown
                    value={props.policy.brokerReferenceGuid && { code: props.policy.brokerReferenceGuid }}
                    references={
                        props.reference.references &&
                        updateChildRef(props.reference.references.ActiveBrokers, policy?.brokerCompanyReferenceGuid)
                    }
                    referencesEvent={() => props.reference.doGetReferences('ActiveBrokers')}
                    changeEvent={(value) => props.assignmentEvent(props.policy.policyTransactionGuid, value.code)}
                    allowEdit
                    blurEvent={() => setAssignable(false)}
                />
            </Focused>
        );

        const assigned = (
            <Container inline top={sizes.xxs}>
                <AvatarContainer>
                    <Avatar firstName={props.policy.brokerFirstName} lastName={policy.brokerLastName} />
                </AvatarContainer>
                <Label
                    text={`${policy.brokerFirstName} ${policy.brokerLastName}`}
                    textAlign={alignments.left}
                    left={sizes.xs}
                />
            </Container>
        );

        const unassigned = (
            <Container inline top={sizes.xxs}>
                <Avatar color={cssVar.colorStrokeLight} firstName={'U'} lastName={'A'} />
                <Label text={unassignedText} textAlign={alignments.left} left={sizes.xs} />
            </Container>
        );

        const assignedBrokerCompany = (
            <Container inline top={sizes.xxs}>
                <Label text={`${policy.brokerCompanyName}`} textAlign={alignments.left} left={sizes.xs} />
            </Container>
        );

        const unassignedBrokerCompany = (
            <Container inline top={sizes.xxs}>
                <Label text={unassignedText} textAlign={alignments.left} left={sizes.xs} />
            </Container>
        );

        return (
            <CardComponent
                left={sizes.xxs}
                right={sizes.xxs}
                color={cssVar.colorLightMode}
                isMobile={screen?.isMobile}
                isTablet={screen?.isTablet}
            >
                <Grid>
                    <Row nogutterbottom>
                        <Column xs={2} md={2} lg={2}>
                            <Container fullSize verticalAlignment={alignments.top}>
                                <ProductClassIcon productClass={policy.productClass} />
                            </Container>
                        </Column>
                        <Column xs={5.5} md={6.5} lg={7.5}>
                            <NameContainer fullSize verticalAlignment={alignments.top}>
                                <Link
                                    textAlign={alignments.left}
                                    text={`${policy.insuredFirstName} ${policy.insuredLastName}`}
                                    href={`${props.context.configuration.policyWeb}/profile?entity=${policy.policyTransactionGuid}`}
                                />
                                <Label bold textAlign={alignments.left} text={policy.submissionReferenceNumber} />
                            </NameContainer>
                        </Column>
                        {(policy.productClass === 'auto' || policy.productClass === 'ppackage') && (
                            <Column xs={2} md={2} lg={2.5}>
                                <Container fullSize verticalAlignment={alignments.top}>
                                    <InsurabilityScoreIcon score={policy.insurabilityScore ?? 0} />
                                </Container>
                            </Column>
                        )}
                    </Row>
                    <Row nogutter>
                        <Column>
                            <Container
                                clickEvent={() =>
                                    setBrokerCompanyAssignable(props.context.session.userType === 'administrator')
                                }
                            >
                                {policy.brokerCompanyReferenceGuid ? assignedBrokerCompany : unassignedBrokerCompany}
                            </Container>
                        </Column>
                    </Row>
                    <Row nogutter>
                        <Column>
                            <Container clickEvent={() => setAssignable(true)}>
                                {assignable ? assignees : policy.brokerReferenceGuid ? assigned : unassigned}
                            </Container>
                        </Column>
                    </Row>
                    <Row nogutter>
                        <Column>
                            <Separator solid top={sizes.xxs} />
                        </Column>
                    </Row>
                    {!collapsed && (
                        <Row nogutterbottom>
                            <Column xs={4}>
                                <Label size={sizes.sm} bold textAlign={alignments.left} text={statusText} />
                            </Column>
                            <Column xs={8}>
                                <Label size={sizes.sm} textAlign={alignments.left} text={policy.transactionStatus} />
                            </Column>

                            <Column xs={4}>
                                <Label size={sizes.sm} bold textAlign={alignments.left} text={productText} />
                            </Column>
                            <Column xs={8}>
                                <Label size={sizes.sm} textAlign={alignments.left} text={policy.productDescription} />
                            </Column>

                            <Column xs={4}>
                                <Label size={sizes.sm} bold textAlign={alignments.left} text={effectiveText} />
                            </Column>
                            <Column xs={8}>
                                <Label size={sizes.sm} textAlign={alignments.left} text={policy.effectiveDate} />
                            </Column>

                            <Column xs={4}>
                                <Label size={sizes.sm} bold textAlign={alignments.left} text={descriptionText} />
                            </Column>
                            <Column xs={8}>
                                <Label size={sizes.sm} textAlign={alignments.left} text={policy.policyDescription} />
                            </Column>

                            {(policy.insuredStreetAddress ||
                                policy.insuredCityProvince ||
                                policy.insuredPostalCode) && (
                                <React.Fragment>
                                    <Column xs={4}>
                                        <Label size={sizes.sm} bold textAlign={alignments.left} text={addressText} />
                                    </Column>
                                    <Column xs={8}>
                                        <Label
                                            size={sizes.sm}
                                            textAlign={alignments.left}
                                            text={policy.insuredStreetAddress}
                                        />
                                        <Label
                                            size={sizes.sm}
                                            textAlign={alignments.left}
                                            text={policy.insuredCityProvince}
                                        />
                                        <Label
                                            size={sizes.sm}
                                            textAlign={alignments.left}
                                            text={policy.insuredPostalCode}
                                        />
                                    </Column>
                                </React.Fragment>
                            )}
                            <Column>
                                <Separator solid top={sizes.xxs} />
                            </Column>

                            <Row nogutterbottom>
                                <Column xs={6}>
                                    <StyledAddPointsSecondayButton
                                        text={callText}
                                        clickEvent={() => {
                                            window.location = `tel:${policy.insuredPhone}`;
                                        }}
                                    />
                                </Column>
                                <Column xs={6}>
                                    <StyledAddPointsSecondayButton
                                        text={emailText}
                                        clickEvent={() => {
                                            window.location = `mailto:${policy.insuredEmail}`;
                                        }}
                                    />
                                </Column>
                            </Row>
                        </Row>
                    )}
                </Grid>
                <Container
                    fullwidth
                    clickEvent={() => setCollapsed(!collapsed)}
                    horizontalAlignment={alignments.center}
                >
                    <ChevronIcon mode={collapsed === true ? 'down' : 'up'} />
                </Container>
            </CardComponent>
        );
    },
    (prevProps, nextProps) => equal(prevProps, nextProps),
);

export default BoardCard;
